import {
    AppstoreOutlined,
    CameraOutlined,
    CheckCircleOutlined,
    CodeSandboxOutlined,
    DollarOutlined,
    InboxOutlined,
    PercentageOutlined,
    PlusCircleOutlined,
    StarOutlined,
    TagOutlined,
    HistoryOutlined,
} from '@ant-design/icons';
import { Form, type FormInstance } from 'antd';
import type { FormProdutoValues } from '@/features/produto/types/FormProdutoValues';
import type { OrdenarLotes } from '@/features/produto/types/OrdenarLotes';
import type { Produto } from '@/features/produto/types/Produto';
import type { TabList } from '@/types';
import type { FormAction } from '@/types/FormAction';
import { useLoginStore } from '@/features/login/store';
import { ProdutoTipo } from '@/features/produto/types/enum';
import { ProdutoTabAdicional } from './adicional/ProdutoTabAdicional';
import { ProdutoTabCaracteristicas } from './caracteristicas/ProdutoTabCaracteristicas';
import { ProdutoTabComissao } from './comissao/ProdutoTabComissao';
import { ProdutoTabComposicaoKit } from './composicao-kit/ProdutoTabComposicaoKit';
import { ProdutoTabEstoque } from './estoque/ProdutoTabEstoque';
import { ProdutoTabFiscal } from './fiscal/ProdutoTabFiscal';
import { ProdutoTabFornecedor } from './fornecedor/ProdutoTabFornecedor';
import { ProdutoTabFotoVideo } from './foto-video/ProdutoTabFotoVideo';
import { ProdutoTabGeral } from './geral/ProdutoTabGeral';
import { ProdutoTabHistoricoAlteracao } from './historico-alteracao/ProdutoTabHistoricoAlteracao';
import { ProdutoTabTabelaPreco } from './tabela-preco/ProdutoTabTabelaPreco';
import { ProdutoTabVariacao } from './variacao/ProdutoTabVariacao';

export enum ProdutoTabKey {
    Geral = 'geral',
    Variacao = 'variacao',
    Fornecedor = 'fornecedor',
    Adicional = 'adicional',
    Estoque = 'estoque',
    ComposicaoKit = 'composicao-kit',
    Caracteristicas = 'caracteristicas',
    FotosVideo = 'fotos-video',
    Comissao = 'comissao',
    TabelaPreco = 'tabela-preco',
    Fiscal = 'fiscal',
    HistoricoAlteracoes = 'historico-alteracoes',
}

export enum ProdutoTabKeyHistoricoAlteracao {
    Geral = 'geral',
    PrecoVenda = 'preco-venda',
    PrecoCusto = 'preco-custo',
    CustoMedio = 'custo-medio',
}

export function useProdutoTabItems({
    form,
    action,
    produto,
    ordenarLotes,
    setFormDirty,
}: {
    form: FormInstance<FormProdutoValues>;
    action: FormAction;
    produto: Produto | undefined;
    ordenarLotes: OrdenarLotes;
    setFormDirty: (dirty: boolean) => void;
}): TabList<ProdutoTabKey>[] {
    const isFintech = useLoginStore((s) => s.isFintech);

    const pro_tipo = Form.useWatch('pro_tipo', form);
    const kit = pro_tipo === ProdutoTipo.Kit;

    const items: TabList<ProdutoTabKey>[] = [
        {
            children: <ProdutoTabGeral action={action} form={form} />,
            forceRender: true,
            icon: <AppstoreOutlined />,
            key: ProdutoTabKey.Geral,
            label: 'Geral',
        },
    ];

    if (pro_tipo === ProdutoTipo.ComAdicional) {
        items.push({
            children: <ProdutoTabAdicional form={form} setFormDirty={setFormDirty} />,
            icon: <PlusCircleOutlined />,
            key: ProdutoTabKey.Adicional,
            label: 'Adicionais',
        });
    }

    if (pro_tipo === ProdutoTipo.ComVariacao) {
        items.push({
            children: <ProdutoTabVariacao form={form} setFormDirty={setFormDirty} />,
            icon: <TagOutlined />,
            key: ProdutoTabKey.Variacao,
            label: 'Variações',
        });
    }

    if (kit) {
        items.push({
            children: <ProdutoTabComposicaoKit form={form} setFormDirty={setFormDirty} />,
            icon: <CodeSandboxOutlined />,
            key: ProdutoTabKey.ComposicaoKit,
            label: 'Composição do kit',
        });
    }

    if (!isFintech && !kit) {
        items.push({
            children: (
                <ProdutoTabEstoque
                    form={form}
                    ordenarLotes={ordenarLotes}
                    setFormDirty={setFormDirty}
                />
            ),
            forceRender: true,
            icon: <CodeSandboxOutlined />,
            key: ProdutoTabKey.Estoque,
            label: 'Estoque',
        });
    }

    if (!isFintech && !kit) {
        items.push({
            children: <ProdutoTabFiscal form={form} />,
            icon: <CheckCircleOutlined />,
            key: ProdutoTabKey.Fiscal,
            label: 'Dados para nota fiscal',
        });
    }

    if (!isFintech) {
        items.push({
            children: <ProdutoTabCaracteristicas />,
            icon: <StarOutlined />,
            key: ProdutoTabKey.Caracteristicas,
            label: 'Características',
        });
    }

    items.push({
        children: <ProdutoTabFotoVideo form={form} setFormDirty={setFormDirty} />,
        icon: <CameraOutlined />,
        key: ProdutoTabKey.FotosVideo,
        label: 'Fotos e vídeo',
    });

    if (!kit) {
        items.push({
            children: <ProdutoTabFornecedor action={action} proIdpk={produto?.pro_idpk} />,
            icon: <InboxOutlined />,
            key: ProdutoTabKey.Fornecedor,
            label: isFintech ? 'Fornecedor' : 'Fornecedores',
        });
    }

    if (action !== 'cadastrar' && produto?.pro_idpk && !isFintech && !kit) {
        items.push({
            children: <ProdutoTabTabelaPreco form={form} produtoIdpk={produto?.pro_idpk} />,
            icon: <DollarOutlined />,
            key: ProdutoTabKey.TabelaPreco,
            label: 'Tabelas de preço',
        });
    }

    if (action !== 'cadastrar' && !isFintech && !kit) {
        items.push({
            children: (
                <ProdutoTabComissao action={action} form={form} setFormDirty={setFormDirty} />
            ),
            icon: <PercentageOutlined />,
            key: ProdutoTabKey.Comissao,
            label: 'Comissões',
        });
    }

    if (action === 'editar') {
        items.push({
            children: <ProdutoTabHistoricoAlteracao form={form} />,
            icon: <HistoryOutlined />,
            key: ProdutoTabKey.HistoricoAlteracoes,
            label: 'Histórico de alterações',
        });
    }

    return items;
}
